import {getTestProps} from '@benchling/js-test-utils';
import {Box, Button, createStyles, Stack, Text} from '@mantine/core';
import React, {useCallback, useEffect, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {getProgressiveDisclosureCategories} from 'llm/chat-library/progressive-disclosure/ProgressiveDisclosureData';
import recordChatUseCaseRun from 'llm/chat-library/use-cases/recordChatUseCaseRun';
import type {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {CenteredSpinner} from 'ui-library/Spinner';
import {warn} from 'util/Logging';
import {assertNever} from 'util/Types';

import {AIOnboardingCompletedScreen} from './AIOnboardingCompletedScreen';
import type {CreateChatResult} from './ChatRouter';
import {OnboardingSkipButton} from './OnboardingComponents';
import {ONBOARDING_SUBTITLE} from './onboardingCopy';
import {OnboardingExamplePrompt, recordOnboardingInterest, suggestOnboardingPrompts} from './OnboardingDAO';
import {OnboardingQuestionView, OnboardingScreenShell} from './OnboardingQuestionView';

const TEST_COMPONENT = 'AIInterestFirstChatScreen';
const INTEREST_QUESTION = 'What are you most interested in using Benchling AI for?';
const NUM_GEMINI_SUGGESTIONS = 3;

/**
 * The static interest options. The first three intentionally map (by stable category `id`, not by
 * label, so progressive-disclosure copy can change freely) to the existing progressive-disclosure
 * categories; "Something else" is handled via the inline free-text chip → Gemini path.
 */
const INTEREST_OPTIONS: ReadonlyArray<{label: string; categoryId: string}> = [
  {label: 'Data capture', categoryId: 'capture-data'},
  {label: 'Report writing', categoryId: 'write-reports'},
  {label: 'Data analysis', categoryId: 'analyze-data'},
];

function getAllExamplePrompts(): Array<OnboardingExamplePrompt> {
  return getProgressiveDisclosureCategories().flatMap((category) =>
    category.options.map((option) => ({id: option.id, label: option.label, prompt: option.prompt}))
  );
}

function getCategoryIdByOptionId(): ReadonlyMap<string, string> {
  return new Map(
    getProgressiveDisclosureCategories().flatMap((category) =>
      category.options.map((option) => [option.id, category.id] as const)
    )
  );
}

function getIconByOptionId(): ReadonlyMap<string, BenchlingIconName> {
  return new Map(
    getProgressiveDisclosureCategories().flatMap((category) =>
      category.options.map((option) => [option.id, category.icon] as const)
    )
  );
}

type Step =
  // The static interest question, presented exactly like an agent-survey question.
  | {kind: 'INTEREST_QUESTION'}
  // The "You're all set" completion page showing the chosen category's progressive-disclosure prompts.
  | {kind: 'CATEGORY_OPTIONS'; categoryId: string}
  // Gemini is minting prompts from the free-text answer.
  | {kind: 'GEMINI_LOADING'}
  // The "You're all set" completion page showing the prompts Gemini selected (carrying their ids).
  | {kind: 'GEMINI_OPTIONS'; options: Array<OnboardingExamplePrompt>}
  // Gemini produced nothing usable — fall back to letting the user pick a category.
  | {kind: 'GEMINI_ERROR'};

interface AIInterestFirstChatScreenProps {
  /** Called with the created chat once the first chat has been submitted. */
  onSubmitFirstChat: (result: CreateChatResult) => void;
  /** Called when the user skips via the footer button (falls back to the settings view). */
  onSkip: () => void;
  /** Called when the user closes via the top-right ✕ (lands on the new chat page). */
  onExit: () => void;
  /**
   * Number of questions the user already answered during the agent survey. This screen is "one more
   * question", so its progress dots continue from that running total.
   */
  progressCount?: number;
  /** Semi-expanded (448px) vs expanded sidepanel width; forwarded to the completion cards. */
  isExpanded?: boolean;
}

/**
 * The final onboarding step: a static interest question that routes the user into their first real
 * AI chat. It renders through the same {@link OnboardingScreenShell} and {@link OnboardingQuestionView}
 * as the agent survey, so it looks like one more survey question.
 *
 * Picking one of the three category chips advances to the {@link AIOnboardingCompletedScreen}
 * ("You're all set") showing that category's progressive-disclosure prompts as cards; typing into the
 * inline "Something else" chip and continuing asks Gemini to mint 3 prompts in the same style, shown on
 * the same completion page. Hovering a card pre-fills the chat input and clicking it submits that
 * prompt as the user's first chat.
 */
export function AIInterestFirstChatScreen({
  onSubmitFirstChat,
  onSkip,
  onExit,
  progressCount = 0,
  isExpanded = true,
}: AIInterestFirstChatScreenProps): JSX.Element {
  const [step, setStep] = useState<Step>({kind: 'INTEREST_QUESTION'});
  // Interest-question state: a single selected category chip and/or the free-text answer.
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [freeTextValue, setFreeTextValue] = useState('');

  useEffect(() => {
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.VIEWED_ONBOARDING_INTEREST_QUESTION);
  }, []);

  // Ask Gemini to select prompts matching the free-text interest answer. ("Went custom" is already
  // tracked via SELECTED_ONBOARDING_INTEREST_OPTION with categoryId 'something_else'.)
  const requestGeminiSuggestions = useCallback((answer: string) => {
    setStep({kind: 'GEMINI_LOADING'});
    suggestOnboardingPrompts(answer, getAllExamplePrompts())
      .then((suggestions) => {
        if (suggestions.length === 0) {
          warn('[llm] Onboarding prompt suggestions returned empty');
          setStep({kind: 'GEMINI_ERROR'});
          return;
        }
        trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.VIEWED_ONBOARDING_GEMINI_SUGGESTIONS, {
          suggestionCount: suggestions.length,
        });
        setStep({kind: 'GEMINI_OPTIONS', options: suggestions.slice(0, NUM_GEMINI_SUGGESTIONS)});
      })
      .catch(() => {
        warn('[llm] Onboarding prompt suggestions request failed');
        setStep({kind: 'GEMINI_ERROR'});
      });
  }, []);

  // Picking a category chip is the answer: record it and advance straight into that category's
  // prompts (no Continue for chips, matching the agent survey).
  const handleInterestSelect = useCallback((categoryId: string) => {
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.SELECTED_ONBOARDING_INTEREST_OPTION, {categoryId});
    const label = INTEREST_OPTIONS.find((o) => o.categoryId === categoryId)?.label;
    if (label != null) {
      recordOnboardingInterest(label).catch(() => {});
    }
    setStep({kind: 'CATEGORY_OPTIONS', categoryId});
  }, []);

  // Continue from the interest question via free text: drive the Gemini path. (Continue only shows
  // when there is free text; chips auto-advance via handleInterestSelect.)
  const handleInterestContinue = useCallback(() => {
    const freeText = freeTextValue.trim();
    if (!freeText) {
      return;
    }
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.SELECTED_ONBOARDING_INTEREST_OPTION, {
      categoryId: 'something_else',
    });
    // Fold the specific free-text interest into About Me (best-effort).
    recordOnboardingInterest(freeText).catch(() => {});
    requestGeminiSuggestions(freeText);
  }, [freeTextValue, requestGeminiSuggestions]);

  return (
    <OnboardingScreenShell testComponent={TEST_COMPONENT} onExit={onExit} exitLabel="Exit onboarding">
      {renderStep()}
    </OnboardingScreenShell>
  );

  function renderStep(): JSX.Element {
    switch (step.kind) {
      case 'INTEREST_QUESTION': {
        // A category chip and free text are mutually exclusive. Picking a chip auto-advances; free
        // text shows a Continue button (mirrors the agent survey's single-answer interaction).
        const hasFreeText = freeTextValue.trim().length > 0;
        return (
          <OnboardingQuestionView
            testComponent={TEST_COMPONENT}
            question={INTEREST_QUESTION}
            subtitle={ONBOARDING_SUBTITLE}
            // This is "one more question" after the agent survey, so continue its running total.
            progressCount={progressCount + 1}
            isExpanded={isExpanded}
            choices={INTEREST_OPTIONS.map((option) => ({
              label: option.label,
              selected: selectedCategoryId === option.categoryId,
              onSelect: () => {
                setFreeTextValue('');
                setSelectedCategoryId(option.categoryId);
                handleInterestSelect(option.categoryId);
              },
            }))}
            freeText={{
              value: freeTextValue,
              onChange: (value) => {
                setSelectedCategoryId(null);
                setFreeTextValue(value);
              },
              onDiscard: () => {
                setFreeTextValue('');
              },
            }}
            footer={
              <Stack spacing="sm" align="center">
                {hasFreeText && (
                  <Button
                    variant="primary"
                    size="sm"
                    radius="xl"
                    onClick={handleInterestContinue}
                    {...getTestProps({component: TEST_COMPONENT, element: 'continue-button'})}
                  >
                    Continue
                  </Button>
                )}
                <OnboardingSkipButton onClick={onSkip} testComponent={TEST_COMPONENT} />
              </Stack>
            }
          />
        );
      }
      case 'CATEGORY_OPTIONS': {
        const category = getProgressiveDisclosureCategories().find((c) => c.id === step.categoryId);
        if (category == null) {
          // Defensive: an unknown category shouldn't happen, but never strand the user.
          return renderCompletedFallback();
        }
        return (
          <AIOnboardingCompletedScreen
            icon={category.icon}
            path="category"
            cards={category.options.map((option) => ({
              label: option.label,
              prompt: option.prompt,
              onSelect: () => {
                trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.SELECTED_ONBOARDING_CATEGORY_PROMPT, {
                  categoryId: category.id,
                  optionId: option.id,
                });
              },
              onSubmitted: () => {
                recordChatUseCaseRun(option.id, {categoryId: category.id, source: 'onboarding'});
              },
            }))}
            onSubmitFirstChat={onSubmitFirstChat}
            onNoAgentAvailable={onSkip}
            isExpanded={isExpanded}
          />
        );
      }
      case 'GEMINI_LOADING':
        return <LoadingView message="Tailoring some ideas for you…" />;
      case 'GEMINI_OPTIONS': {
        const categoryIdByOptionId = getCategoryIdByOptionId();
        const iconByOptionId = getIconByOptionId();
        return (
          <AIOnboardingCompletedScreen
            // Fall back to the sparkle icon if a suggestion's id is somehow unknown.
            icon="sparkle"
            path="gemini"
            cards={step.options.map((option) => ({
              label: option.label,
              prompt: option.prompt,
              // Show the icon of the progressive-disclosure category this suggestion is a child of, so
              // each card carries its own category's icon rather than a single shared sparkle.
              icon: iconByOptionId.get(option.id),
              // Gemini selects from the static progressive-disclosure options, so report the same
              // {categoryId, optionId} as a directly-picked category prompt.
              onSelect: () => {
                trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.SELECTED_ONBOARDING_CATEGORY_PROMPT, {
                  categoryId: categoryIdByOptionId.get(option.id),
                  optionId: option.id,
                });
              },
              onSubmitted: () => {
                recordChatUseCaseRun(option.id, {
                  categoryId: categoryIdByOptionId.get(option.id),
                  source: 'onboarding',
                });
              },
            }))}
            onSubmitFirstChat={onSubmitFirstChat}
            onNoAgentAvailable={onSkip}
            isExpanded={isExpanded}
          />
        );
      }
      case 'GEMINI_ERROR':
        return renderCompletedFallback();
      default:
        return assertNever(step);
    }
  }

  // When Gemini can't produce suggestions, land on the same "You're all set" completion page — just
  // without prompt cards — so the user can still type their first message rather than seeing a
  // different, confusing screen.
  function renderCompletedFallback(): JSX.Element {
    return (
      <AIOnboardingCompletedScreen
        onSubmitFirstChat={onSubmitFirstChat}
        onNoAgentAvailable={onSkip}
        isExpanded={isExpanded}
      />
    );
  }
}

function LoadingView({message}: {message: string}): JSX.Element {
  const {classes} = useStyles();
  return (
    <Box className={classes.body}>
      <Stack align="center" spacing="sm" className={classes.loadingState}>
        <CenteredSpinner />
        <Text className={classes.loadingText}>{message}</Text>
      </Stack>
    </Box>
  );
}

const useStyles = createStyles((theme) => ({
  body: {
    flex: 1,
    overflowY: 'auto',
  },
  loadingState: {
    padding: `${theme.spacing.lg} 0`,
  },
  loadingText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.gray[6],
    textAlign: 'center',
  },
}));

// Exposed for unit testing the static interest → category mapping.
export const _INTEREST_OPTIONS = INTEREST_OPTIONS;
